import React, { useEffect, useState } from "react";
import { motion, useInView, useMotionValue, animate } from "framer-motion";
import SEO from "../../seo/SEO";
import { seoConfig } from "../../seo/seoConfig";
import {
  TbRocket,
  TbAtom,
  TbFileInvoice,
  TbUsersGroup,
  TbNetwork,
  TbSparkles,
  TbChartArrowsVertical,
  TbUserPlus,
  TbChartBar,
  TbDeviceMobile,
  TbBell,
  TbLanguage,
  TbDevices,
  TbCash,
  TbSpeakerphone,
  TbChartLine,
  TbBox,
  TbBus,
  TbBook2,
  TbHome,
  TbReceipt,
  TbCalendarEvent,
} from "react-icons/tb";
import { HiArrowRight } from "react-icons/hi";
import Badge from "../../components/Badge/Badge";
import Card from "../../components/Card/Card";
import FeatureIcon from "../../components/FeatureIcon/FeatureIcon";
import Button from "../../components/Button/Button";
import styles from "./PlatformFeatures.module.css";
import { TbCloud, TbRadio, TbDeviceDesktop } from "react-icons/tb";
// ✅ Add this if not already there
import LazyImage from "../../components/LazyImage/LazyImage";

// ❌ Remove the old direct import for dashboardImg
// import dashboardImg from "../../assets/images/...";

// ✅ Add this (alias keeps the dashboardImg variable name working)
import { ifillip_feature_image2 as dashboardImg } from "../../controller/Image";
import MobileAppSection from "../../components/MobileAppSection/MobileAppSection";

const ECOSYSTEM_FEATURES = [
  {
    icon: TbCloud,
    title: "Cloud-Enabled Platform",
    text: "iFillip is a cloud-enabled platform with a secure and scalable infrastructure. It ensures real-time data availability, smooth collaboration across stakeholders, and seamless data synchronization.",
  },
  {
    icon: TbRadio,
    title: "RFID-Based Attendance",
    text: "With the RFID (Radio Frequency Identification) approach, iFillip automates students' daily attendance by enabling real-time attendance tracking and syncing data within the student attendance management software, keeping teachers and parents notified.",
  },
  {
    icon: TbDeviceDesktop,
    title: "Digitalize Your Workflows",
    text: "With iFillip, reduce paperwork and eliminate human errors. Switch smarter and streamline your academic workflows such as admissions, attendance, and performance digitally. iFillip can be accessed quickly across all devices and operating systems, offering a fast and mobile-friendly platform.",
  },
];
const UTILITIES = [
  { icon: TbBox, label: "Inventory" },
  { icon: TbBus, label: "Transport" },
  { icon: TbBook2, label: "Library" },
  { icon: TbHome, label: "Hostel" },
  { icon: TbReceipt, label: "Payroll" },
  { icon: TbCalendarEvent, label: "Events" },
];

/* ============================================================
   COUNT-UP NUMBER   uses Framer's useInView + animate()
   Same "once-only" viewport detection concept as <motion.rect>
============================================================ */

const CountUpNumber = ({
  target,
  duration = 1.6,
  decimals = 0,
  prefix = "",
  suffix = "",
}) => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const controls = animate(0, target, {
      duration,
      ease: "easeOut",
      onUpdate: (latest) => {
        setValue(
          decimals > 0
            ? parseFloat(latest.toFixed(decimals))
            : Math.round(latest),
        );
      },
    });
    return () => controls.stop();
  }, [isInView, target, duration, decimals]);

  const display = decimals > 0 ? value.toFixed(decimals) : value;
  return (
    <span ref={ref}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

/* ============================================================
   CHARTS   using Framer's whileInView directly (same as AI Features page)
============================================================ */

const GrowthLine = () => (
  <svg
    viewBox="0 0 200 120"
    className={styles.growthChart}
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="growthFill" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.25" />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
      </linearGradient>
    </defs>
    <motion.path
      d="M0 100 L30 85 L60 80 L90 60 L120 55 L150 30 L200 18 L200 120 L0 120 Z"
      fill="url(#growthFill)"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1, delay: 0.6 }}
    />
    <motion.path
      d="M0 100 L30 85 L60 80 L90 60 L120 55 L150 30 L200 18"
      stroke="#8B5CF6"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      whileInView={{ pathLength: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.4, ease: "easeOut" }}
    />
  </svg>
);

const FiscalBars = () => {
  const bars = [
    { x: 10, h: 30, c: "#A78BFA" },
    { x: 35, h: 20, c: "#C4B5FD" },
    { x: 60, h: 50, c: "#8B5CF6" },
    { x: 85, h: 40, c: "#A78BFA" },
  ];
  return (
    <svg
      viewBox="0 0 120 80"
      className={styles.fiscalChart}
      xmlns="http://www.w3.org/2000/svg"
    >
      {bars.map((b, i) => (
        <motion.rect
          key={i}
          x={b.x}
          y={70 - b.h}
          width="18"
          height={b.h}
          rx="3"
          fill={b.c}
          initial={{ scaleY: 0 }}
          whileInView={{ scaleY: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: i * 0.1, ease: "easeOut" }}
          style={{ transformOrigin: `${b.x + 9}px 70px` }}
        />
      ))}
    </svg>
  );
};

const PlatformOverview = () => {
  return (
    <>
      <SEO {...seoConfig.coreFeatures} />{" "}
      <div className={styles.page}>
        {/* ============ HERO ============ */}
        {/* ============ UNIFIED HERO + SYNTHESIS ============ */}
        {/* ============ CORE FEATURES (single section) ============ */}
        <section className={`page-container ${styles.heroSection}`}>
          {/* --- Section title + sub text (UNCHANGED) --- */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="cyan">Complete School Suite</Badge>
            <h1 className={styles.heroTitle}>
              Lead the future of education with{" "}
              <span className="gradientText">
                comprehensive smart learning management system.
              </span>
            </h1>
            <p className={styles.heroLead}>
              Most educational institutions in India are using digital
              technology in some way or the other in their day-to-day
              operations. In recent times, these institutions have started
              increasingly using AI as part of their digital journey. Gone are
              the days wherein schools used basic digitalization for automating
              their internal processes. Schools are now opting for intelligent
              and integrated platforms like iFillip that streamline academic
              operations, increase productivity and support better learning
              outcomes.
            </p>
            <p className={`mb-4 ${styles.heroLead}`}>
              iFillip is an AI-driven next-gen learning management system
              designed specifically for Indian schools. It combines smart
              digital tools to strengthen academic processes and enhance
              learning experiences enabling teachers to increase student
              engagement and improve overall school performance.
            </p>
          </motion.div>

          {/* --- Row 1: showcase cards (6 / 6 / 12) --- */}
          <div className={`row g-4 ${styles.heroCardsRow}`}>
            {/* AI-Assisted Teaching */}
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card variant="default" padding="lg" className={styles.synCard}>
                  <FeatureIcon icon={TbAtom} tint="purple" size="lg" />
                  <h3 className="subTitle">AI-Assisted Teaching</h3>
                  <p className={styles.synText}>
                    iFillip has an AI-driven feature that helps create lesson
                    plans, generate quizzes, prepare study materials and gives
                    smart inputs reducing administrative workload. Teachers
                    spend more time on student engagement improving their
                    learning experiences.
                  </p>
                </Card>
              </motion.div>
            </div>

            {/* Role-Based Smart Dashboards & Analytics (retains Data-Driven Insights content + stat) */}
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card
                  variant="gradient"
                  padding="lg"
                  className={styles.growthCard}
                >
                  <FeatureIcon icon={TbUsersGroup} tint="dark" size="lg" />
                  <h3 className="subTitle">
                    Role-Based Smart Dashboards &amp; Analytics
                  </h3>
                  <p className={styles.growthText}>
                    Get personalized dashboards for teachers, principals,
                    administrators, and management. AI-powered analytics give
                    real-time visibility into individual student & overall class
                    performance to plan focused interventions
                  </p>
                  <div className={styles.growthStat}>
                    <div className={styles.growthStatValue}>
                      <CountUpNumber target={24} prefix="+" suffix="%" />
                    </div>
                    <div className={styles.growthStatLabel}>
                      AVG. ENROLLMENT CONVERSION INCREASE
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* AI-Driven Adaptive Learning (retains original full-width card + risk stats + GrowthLine) */}
            <div className="col-12">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.riskCard}
                >
                  <div className="row g-3 align-items-center">
                    <div className="col-12 col-sm-7">
                      <Badge variant="purple">Predictive Insights</Badge>
                      <h3 className="subTitle" style={{ marginTop: "0.75rem" }}>
                        AI-Driven Adaptive Learning
                      </h3>
                      <p className={styles.synText}>
                        To cater to the pace of individual students, iFillip`s
                        intuitive analytics engine provides for personalized
                        learning experiences – curated lessons, learning paths
                        and assessments – allowing for continuous tracking of
                        progress and improved performance.
                      </p>
                      <div className={styles.riskStats}>
                        <div className={styles.riskStat}>
                          <div className={styles.riskLabel}>Risk Level</div>
                          <div className={styles.riskValueDanger}>
                            Needs Attention
                          </div>
                        </div>
                        <div className={styles.riskStat}>
                          <div className={styles.riskLabel}>Accuracy</div>
                          <div className={styles.riskValue}>
                            <CountUpNumber target={91} suffix="%" />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-5 text-center">
                      <div className={styles.growthWrap}>
                        <GrowthLine />
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>

          {/* --- Row 2: modules (8 / 4) --- */}
          <div className="row g-4 mt-3">
            {/* Online Admissions Management (retains Online Admissions content) */}
            <div className="col-12 col-lg-8">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbUserPlus} tint="purple" size="lg" />
                  <h3 className={styles.moduleTitle}>
                    Online Admissions Management
                  </h3>
                  <p className={styles.moduleText}>
                    Digitize the entire process, from enquiry to admissions.
                    Simplify application submission, document collection and
                    application tracking through a digital platform that
                    improves efficiency and enhances the overall experience.
                  </p>
                </Card>
              </motion.div>
            </div>

            {/* IoT-Based Attendance Management (retains RFID Attendance content + uptime) */}
            <div className="col-12 col-lg-4">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbDeviceMobile} tint="purple" size="lg" />
                  <h3 className={styles.moduleTitle}>
                    IoT-Based Attendance Management
                  </h3>
                  <p className={styles.moduleText}>
                    Automate attendance with contactless IoT-enabled tracking
                    across school premises and transportation systems. Real-time
                    notifications keep parents informed whenever students arrive
                    at or leave school.
                  </p>
                  <div className={styles.uptimeRow}>
                    <span className={styles.uptimeLabel}>Live Sync</span>
                    <span className={styles.uptimeValue}>
                      <CountUpNumber
                        target={99.9}
                        decimals={1}
                        suffix="% Uptime"
                      />
                    </span>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>

          {/* --- Row 3: modules (4 / 4 / 4) --- */}
          <div className={`row mt-3 g-4 ${styles.row2}`}>
            {/* Examination Management (retains Exams & Assessments content + pill) */}
            <div className="col-12 col-md-6 col-lg-4">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbChartBar} tint="cyan" size="lg" />
                  <h3 className="subTitle">Examination Management</h3>
                  <p className={styles.moduleText}>
                    Manage every stage of the examination process through a
                    single platform. From announcing exam schedules and
                    recording marks to publishing results, iFillip streamlines
                    examination management while helping schools ensure
                    accuracy, transparency, and improve operational efficiency.
                  </p>
                  <div className={styles.availablePill}>
                    <motion.span
                      className={styles.dot}
                      animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    />{" "}
                    New: AI Proctoring Available
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* News, Alerts, Events & Announcements (takes the dark-card slot) */}
            <div className="col-12 col-md-6 col-lg-4">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card
                  variant="dark"
                  padding="lg"
                  className={styles.moduleCardDark}
                >
                  <FeatureIcon icon={TbBell} tint="dark" size="lg" />
                  <h3 className="subTitle">
                    News, Alerts, Events &amp; Announcements
                  </h3>
                  <p className={styles.moduleTextDark}>
                    Keep students, parents and staff informed round the clock
                    with all the latest alerts & announcements. Share circulars,
                    important notifications, academic schedules and
                    event-related details keeping all stakeholders informed.
                  </p>
                </Card>
              </motion.div>
            </div>

            {/* Stakeholder Communication Hub (retains Parent & School Communication content + button) */}
            <div className="col-12 col-md-6 col-lg-4">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbSpeakerphone} tint="pink" size="lg" />
                  <h3 className="subTitle">Stakeholder Communication Hub</h3>
                  <p className={styles.moduleText}>
                    Multi-modal communication channels – email, push
                    notifications and in-app messaging – ensure seamless
                    communication across stakeholders. Deliver personalized
                    updates in preferred languages while tracking message
                    delivery and engagement.
                  </p>
                  {/* <Button variant="dark" size="sm">
                  Manage Channels
                </Button> */}
                </Card>
              </motion.div>
            </div>
          </div>

          {/* --- Row 4: remaining features (6 / 6) — same moduleCard style --- */}
          <div className="row mt-3 g-4">
            {/* Multilingual Enablement */}
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbLanguage} tint="cyan" size="lg" />
                  <h3 className={styles.moduleTitle}>
                    Multilingual Enablement
                  </h3>
                  <p className={styles.moduleText}>
                    Empower students and parents with language-friendly access
                    to education. iFillip supports English and major regional
                    languages, making learning resources, academic updates and
                    school communications accessible to users in their preferred
                    language.
                  </p>
                </Card>
              </motion.div>
            </div>

            {/* Omnichannel Access Across All Devices */}
            <div className="col-12 col-lg-6">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <Card
                  variant="default"
                  padding="lg"
                  className={styles.moduleCard}
                >
                  <FeatureIcon icon={TbDevices} tint="purple" size="lg" />
                  <h3 className={styles.moduleTitle}>Fee Management</h3>
                  <p className={styles.moduleText}>
                    Enable hassle-free fee collection through integrated online
                    payment gateways and automated payment workflows. With
                    real-time transaction tracking, instant receipts and faster
                    reconciliation, iFillip helps schools streamline financial
                    operations and improve collection efficiency.
                  </p>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ============ Mobile Application Features Section ============ */}

        <MobileAppSection />
        {/* ============ DEPARTMENTAL SYNTHESIS ============ */}
        {/* <section className={`page-container ${styles.synthesis}`}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h1 className={styles.heroTitle}>
            <span className="gradientText">
              The Future of Education and School Management
            </span>{" "}
            Starts Here
          </h1>
          <p className={`mb-2 ${styles.sectionLead}`}>
            Manage all academic operations efficiently, such as IoT-based
            attendance tracking, academic operations, online admissions, study
            material resources, analytics insights, class timetables, and exam
            schedules with ease.
          </p>
          <p className={`${styles.sectionLead}`}>
            Transformation of Education from AI based personalizdd teaching,
            adaptive learning and AI based predictions & recommendations to
            ensure individual student level real learning and perceptible
            improvements instead of lecturing and memorising
          </p>
        </motion.div>

        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card variant="default" padding="lg" className={styles.synCard}>
                <FeatureIcon icon={TbAtom} tint="purple" size="lg" />
                <h3 className="subTitle">Academic Operations</h3>
                <p className={styles.synText}>
                  Handle class schedules, exam planning, and student performance
                  tracking from a single platform and efficient automated
                  workflows to keep teachers and principals are well informed
                </p>
              </Card>
            </motion.div>
          </div>

          <div className="col-12 col-lg-6">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card variant="default" padding="lg" className={styles.synCard}>
                <FeatureIcon icon={TbFileInvoice} tint="purple" size="lg" />
                <h3 className="subTitle">Online Admissions</h3>
                <p className={styles.synText}>
                  Digitize the regular admissions process through automated work
                  flows and track every application from submission to
                  enrollment, all in one organised form. Eliminate traditional
                  cumbersome paperwork and reduce manual errors, streamline
                  Communication with applicants directly faster and more
                  transparent.
                </p>
                <div className={styles.fiscalWrap}>
                  <FiscalBars />
                </div>
              </Card>
            </motion.div>
          </div>

          <div className="col-12 col-md-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <Card
                variant="gradient"
                padding="lg"
                className={styles.growthCard}
              >
                <FeatureIcon icon={TbUsersGroup} tint="dark" size="lg" />
                <h3 className="subTitle">Data-driven insights</h3>
                <p className={styles.growthText}>
                  With iFillip, identify the both students’ and teachers’
                  real-time academic performances, along with detailed classroom
                  demographics through advanced AI-powered data analytics with
                  metrics and Statistical numbers and make informed decisions
                  that helps you to track progress and improve the overall
                  academic outcomes
                </p>
                <div className={styles.growthStat}>
                  <div className={styles.growthStatValue}>
                    <CountUpNumber target={24} prefix="+" suffix="%" />
                  </div>
                  <div className={styles.growthStatLabel}>
                    AVG. ENROLLMENT CONVERSION INCREASE
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>

          <div className="col-12 col-md-7">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Card variant="default" padding="lg" className={styles.riskCard}>
                <div className="row g-3 align-items-start">
                  <div className="col-12 col-sm-7">
                    <Badge variant="purple">Predictive Insights</Badge>
                    <h3 className="subTitle" style={{ marginTop: "0.75rem" }}>
                      AI-Driven Adaptive Education
                    </h3>
                    <p className={styles.synText}>
                      Featuring an AI-powered adaptive learning platform,
                      iFillip enables teachers to create lesson plans,
                      assessments, quizzes, and study materials, students can
                      actively evaluate their learning, improve performance
                      based on the assessments.
                    </p>
                    <div className={styles.riskStats}>
                      <div className={styles.riskStat}>
                        <div className={styles.riskLabel}>Risk Level</div>
                        <div className={styles.riskValueDanger}>
                          Needs Attention
                        </div>
                      </div>
                      <div className={styles.riskStat}>
                        <div className={styles.riskLabel}>Accuracy</div>
                        <div className={styles.riskValue}>
                          <CountUpNumber target={91} suffix="%" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-5 text-center">
                    <div className={styles.growthWrap}>
                      <GrowthLine />
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </section> */}

        {/* ============ REPORTS & ANALYTICS ============ */}
        {/* <section className={`page-container ${styles.reportsSection}`}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Card variant="default" padding="lg" className={styles.reportsCard}>
            <div className="row align-items-center g-4">
              <div className="col-12 col-md-6">
                <FeatureIcon icon={TbChartLine} tint="cyan" size="lg" />
                <h3 className="subTitle">Reports &amp; Analytics</h3>
                <p className={styles.reportsText}>
                  Build the reports you actually need. iFillip's dashboard gives
                  leadership real-time visibility across academics, finance, and
                  operations with the ability to drill down to individual
                  classes, teachers, or students in seconds.
                </p>
              </div>
              <div className="col-12 col-md-6 text-center">
                <LazyImage
                  {...dashboardImg}
                  alt="Analytics dashboard preview"
                  wrapperClassName={styles.dashboardImg}
                />
              </div>
            </div>
          </Card>
        </motion.div>
      </section> */}
        {/* ============ SUPPORTING TOOLS FOR EVERYDAY OPERATIONS ============ */}
        {/* <section className={`page-container ${styles.utilities}`}>
        <h2 className={styles.utilitiesTitle}>
          Supporting Tools for Everyday Operationssss
        </h2>
        <div className="row g-3 g-lg-4">
          {UTILITIES.map((u, i) => (
            <div key={u.label} className="col-6 col-md-4 col-lg-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Card
                  variant="default"
                  padding="md"
                  className={styles.utilCard}
                >
                  <u.icon className={styles.utilIcon} />
                  <span className={styles.utilLabel}>{u.label}</span>
                </Card>
              </motion.div>
            </div>
          ))}
        </div>
      </section> */}
      </div>
    </>
  );
};

export default PlatformOverview;
